import { useContext } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import {
  CYRILLIC_REGEXP,
  getAddressRegExpByProviderAndMethod,
  getCityRegExp,
  getRegistrationNumberRegExp,
  hasAtLeastTwoUniqueCharacters
} from 'components/common/PaymentForm/ProviderForms/ModelValidators/utils';
import { PAYMENT_METHOD, PAYMENT_PROVIDERS } from 'components/common/constants';
import { getConveraRequirements } from 'components/common/PaymentForm/converaRequirements';
import i18nContext from 'components/i18n-context';
//eslint-disable-next-line max-len
import ValidateRecipientAdditionalDetailsModel from 'components/NewPaymentFormV2/models/ValidateRecipientAdditionalDetailsModel';
import Button from 'uikit/Button/Button';
import Input from 'uikit/Input/Input';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';
import './RecipientAdditionalDetailsStep.scss';
import { getNewPaymentFormStepErrorMessage } from 'components/NewPaymentFormV2/utils/getNewPaymentFormStepErrorMessage';
import { isBeneficiaryStateProvider, trimTextContent } from '../../../../../../services/utils';

const RecipientAdditionalDetailsStep = ({ newPaymentStore, customerNumber, userAccounts }) => {
  const i18n = useContext(i18nContext);
  const isBeneficiaryStateAvailable = isBeneficiaryStateProvider(newPaymentStore.currentAccount?.transfer_provider);
  // TRN-7602: for Convera API / Merchant the State is mandatory for some beneficiary BANK countries (the one chosen
  // on the bank details step — or taken from the IBAN there).
  const { isStateRequired } = getConveraRequirements({
    provider: newPaymentStore.currentAccount?.transfer_provider,
    paymentMethod:
      newPaymentStore.paymentDetailsStep?.paymentMethod || newPaymentStore.accountDetailsStep?.paymentMethod,
    bankCountry: newPaymentStore.bankDetailsStep?.bankCountry,
    hasIban: !!newPaymentStore.accountDetailsStep?.iban
  });

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      registrationNumber: newPaymentStore.recipientAdditionalDetailsStep?.registrationNumber || '',
      country: newPaymentStore.recipientAdditionalDetailsStep?.country || '',
      city: newPaymentStore.recipientAdditionalDetailsStep?.city || '',
      postCode: newPaymentStore.recipientAdditionalDetailsStep?.postCode || '',
      state: newPaymentStore.recipientAdditionalDetailsStep?.state || '',
      address: newPaymentStore.recipientAdditionalDetailsStep?.address || ''
    },
    validationSchema: Yup.object().shape({
      registrationNumber: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(35, i18n.getMessage('client.validation.message.maxSize', { size: 35 }))
        .matches(getRegistrationNumberRegExp(), {
          message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
            availableSymbols: "“.“, “,“, “-“, “/“, “(“, “)“, “'“, “+“, “Space“"
          })
        }),
      country: Yup.string().trim().required(i18n.getMessage('client.validation.message.isEmpty')),
      city: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(35, i18n.getMessage('client.validation.message.maxSize', { size: 35 }))
        .matches(getCityRegExp(), {
          message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
            availableSymbols: '“-“'
          })
        }),
      postCode: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(15, i18n.getMessage('client.validation.message.maxSize', { size: 15 }))
        .test(
          'no-cyrillic',
          i18n.getMessage('client.validation.message.noCyrillic'),
          (value) => !CYRILLIC_REGEXP.test(value || '')
        ),
      state: Yup.string()
        .trim()
        .max(100, i18n.getMessage('client.validation.message.maxSize', { size: 100 }))
        .test(
          'no-cyrillic',
          i18n.getMessage('client.validation.message.noCyrillic'),
          (value) => !CYRILLIC_REGEXP.test(value || '')
        )
        .test('state-required', i18n.getMessage('client.validation.message.isEmpty'), (value) =>
          isStateRequired ? !!(value || '').trim() : true
        ),
      address: Yup.string()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .min(2, i18n.getMessage('client.validation.message.minSize', { size: 2 }))
        .max(140, i18n.getMessage('client.validation.message.maxSize', { size: 140 }))
        .test('address-validation', function (value) {
          const paymentMethod = newPaymentStore.accountDetailsStep?.paymentMethod;
          const providerType = newPaymentStore.accountDetailsStep?.senderAccount?.transfer_provider;
          const restrictedProviders = [
            PAYMENT_PROVIDERS.MANUAL,
            PAYMENT_PROVIDERS.CONVERA,
            PAYMENT_PROVIDERS.CONVERA_MERCHANT,
            PAYMENT_PROVIDERS.CONVERA_API
          ];

          if (!value) return true;

          // Check for at least two unique characters
          if (!hasAtLeastTwoUniqueCharacters(trimTextContent(value))) {
            return this.createError({ message: i18n.getMessage('transfer.form.error.address') });
          }

          // Check regex pattern based on provider and method
          const addressRegExp = getAddressRegExpByProviderAndMethod(providerType, paymentMethod);
          if (!addressRegExp.test(value)) {
            const errorMessage =
              paymentMethod === PAYMENT_METHOD.SWIFT && restrictedProviders.includes(providerType)
                ? i18n.getMessage('transfer.form.error.address.swift.restricted')
                : i18n.getMessage('transfer.form.error.address');
            return this.createError({ message: errorMessage });
          }

          return true;
        })
    }),
    onSubmit: async (values) => {
      newPaymentStore.setError(null);

      const requestData = ValidateRecipientAdditionalDetailsModel({
        ...newPaymentStore.recipientAdditionalDetailsStep,
        ...values
      });

      newPaymentStore.updateRecipientAdditionalDetailsStep(values);

      await newPaymentStore.validateAdditionalRecipientDetails(customerNumber, requestData);
    }
  });

  const { values, errors, handleSubmit, handleChange } = form;

  const allCodes = userAccounts?.flatMap((acct) => acct.countries) ?? [];
  const uniqueCodes = [...new Set(allCodes)];
  const countriesOptions = uniqueCodes.map((code) => ({
    key: code,
    value: i18n.getMessage(`country.${code}`)
  }));

  const handleCountryChange = (name, data) => handleChange({ target: { name, value: data } });

  return (
    <form onSubmit={form.handleSubmit} className={'new-payment-recipient-additional-details-step'}>
      <div className={'new-payment-recipient-additional-details-step-inputs-wrapper'}>
        <Input
          className={'new-payment-recipient-additional-details-step-input'}
          name={'registrationNumber'}
          label={i18n.getMessage('templates.form.registrationNumber.label')}
          placeholder={i18n.getMessage('templates.form.registrationNumber.placeholder')}
          value={values.registrationNumber}
          error={
            errors.registrationNumber ||
            getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'registrationNumber')
          }
          onChange={handleChange}
          isRequired
        />
        <InputDropDownSearch
          autoComplete={'off'}
          className={'new-payment-recipient-additional-details-step-input'}
          name={'country'}
          label={i18n.getMessage('templates.form.country.label')}
          placeholder={i18n.getMessage('templates.form.country.placeholder')}
          options={countriesOptions}
          value={values.country}
          error={errors.country || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'country')}
          onChange={handleCountryChange}
          isRequired
        />
        <Input
          className={'new-payment-recipient-additional-details-step-input'}
          name={'city'}
          label={i18n.getMessage('templates.form.city.label')}
          placeholder={i18n.getMessage('templates.form.city.placeholder')}
          value={values.city}
          error={errors.city || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'city')}
          onChange={handleChange}
          isRequired
        />
        <Input
          className={'new-payment-recipient-additional-details-step-input'}
          name={'postCode'}
          label={i18n.getMessage('templates.form.postCode.label')}
          placeholder={i18n.getMessage('templates.form.postCode.placeholder')}
          value={values.postCode}
          error={errors.postCode || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'postCode')}
          onChange={handleChange}
          isRequired
        />
        {isBeneficiaryStateAvailable && (
          <Input
            className={'new-payment-recipient-additional-details-step-input'}
            name={'state'}
            label={i18n.getMessage('templates.form.state.label')}
            placeholder={i18n.getMessage('templates.form.state.placeholder')}
            value={values.state}
            error={errors.state || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'state')}
            onChange={handleChange}
            isRequired={isStateRequired}
          />
        )}
        <Input
          className={'new-payment-recipient-additional-details-step-input'}
          name={'address'}
          label={i18n.getMessage('templates.form.address.label')}
          placeholder={i18n.getMessage('templates.form.address.placeholder')}
          value={values.address}
          error={errors.address || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'address')}
          onChange={handleChange}
          isRequired
        />
      </div>
      <div className={'new-payment-step-buttons'}>
        <Button
          className={'new-payment-recipient-additional-details-step-next-button'}
          type={'primary'}
          roleType={'submit'}
          onClick={handleSubmit}
          isDisabled={newPaymentStore.isLoading}
        >
          {i18n.getMessage('templates.form.button.next')}
        </Button>
      </div>
    </form>
  );
};

RecipientAdditionalDetailsStep.propTypes = {
  customerNumber: PropTypes.string,
  userAccounts: PropTypes.array,
  newPaymentStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  newPaymentStore: stores.newPaymentStore
}))(observer(RecipientAdditionalDetailsStep));
